/**
 * Timezone-aware date formatting.
 *
 * All server timestamps are UTC ISO strings. `new Date("2026-04-23")` (date-only)
 * is treated as UTC midnight by JS, which shifts it one day behind in negative-offset
 * timezones (EST, CST). We detect date-only strings and parse them as local midnight
 * so "Apr 23" always means Apr 23 regardless of timezone.
 *
 * Full datetime strings (with Z or offset) are rendered via formatInTimeZone so the
 * user's local timezone is always applied explicitly.
 */

import { format, formatDistanceToNow, isValid } from 'date-fns'
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz'
import { getWorkspaceTimezone } from '@/lib/workspace'

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/
const FALLBACK_TZ = 'America/Chicago'

export function localTz(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

/** Browser-detected IANA timezone — used by the Settings picker hint. */
export function browserTz(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

function resolveTz(tz?: string | null): string {
  const candidate = tz || getWorkspaceTimezone() || FALLBACK_TZ
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: candidate })
    return candidate
  } catch {
    return FALLBACK_TZ
  }
}

/**
 * Convert a `<input type="datetime-local">` value ("YYYY-MM-DDTHH:mm") into the
 * UTC instant it denotes **in the workspace timezone**.
 *
 * `new Date("2026-09-10T09:00")` interprets the value in the BROWSER's zone.
 * For anything scheduling-related that is the wrong reading: the operator is
 * booking 9 AM where the business is, not 9 AM where their laptop happens to
 * be. Sending the browser-derived instant to the API silently stored a time
 * shifted by the browser↔workspace offset (FND-TZ-12).
 *
 * Returns null when the value isn't a parseable wall-clock string.
 */
export function wallClockToUtc(value: string, tz?: string | null): Date | null {
  if (!value) return null
  try {
    const d = zonedTimeToUtc(value, resolveTz(tz))
    return isValid(d) ? d : null
  } catch {
    return null
  }
}

/**
 * Format an instant as a `<input type="datetime-local">` value expressing the
 * WORKSPACE wall-clock time.
 *
 * The inverse of `wallClockToUtc`, and the reason it exists: once the form's
 * inputs mean workspace time, prefilling them from a browser-local Date breaks
 * the round-trip. Clicking a 9 AM calendar slot prefilled "09:00" read off the
 * browser's clock, which the API then interpreted as 9 AM in the workspace —
 * so the saved event landed somewhere other than the slot that was clicked.
 */
export function toWorkspaceWallClockInput(
  input: Date | string | null | undefined,
  tz?: string | null,
): string {
  if (!input) return ''
  const d = typeof input === 'string' ? new Date(input) : input
  if (!isValid(d)) return ''
  return formatInTimeZone(d, resolveTz(tz), "yyyy-MM-dd'T'HH:mm")
}

/**
 * Browser-local Date range for rendering an all-day event on a browser-local
 * calendar grid (react-big-calendar).
 *
 * All-day events are persisted as workspace-midnight → *exclusive*
 * next-midnight instants. Handing those raw instants to a grid that lays out
 * in browser-local time produces two artefacts:
 *   - west of the workspace zone the band starts on the previous day, and
 *   - the exclusive end bleeds the band into one extra day-cell.
 *
 * So re-anchor: take the workspace-zone calendar dates the event actually
 * covers, then rebuild them as browser-local midnight → end-of-last-day. The
 * band then covers the same date squares the workspace sees, in every zone.
 */
export function allDayGridRange(
  startAt: string,
  endAt: string,
  tz?: string | null,
): { start: Date; end: Date } | null {
  const zone = resolveTz(tz)
  const startInstant = new Date(startAt)
  const endInstant = new Date(endAt)
  if (!isValid(startInstant) || !isValid(endInstant)) return null
  // Step back off the exclusive end so we land on the last *covered* day. Rows
  // written before the exclusive-end fix can have end <= start; clamp those to
  // the start day rather than rendering a negative range.
  const lastCovered = new Date(Math.max(endInstant.getTime() - 1, startInstant.getTime()))
  const firstDay = formatInTimeZone(startInstant, zone, 'yyyy-MM-dd')
  const lastDay = formatInTimeZone(lastCovered, zone, 'yyyy-MM-dd')
  return {
    start: new Date(`${firstDay}T00:00:00`),
    end: new Date(`${lastDay}T23:59:59.999`),
  }
}

/**
 * Format a date in the workspace timezone (preferred for any date that affects
 * scheduling or business reporting). Defaults to the cached workspace timezone.
 *
 * - Date-only strings ("YYYY-MM-DD") render literally (no tz conversion) so an
 *   all-day event on 2026-03-15 never displays as March 14.
 * - Full datetime values are converted from UTC to the workspace TZ.
 */
export function formatInWorkspaceTimezone(
  input: string | Date | null | undefined,
  fmt = 'MMM d, yyyy',
  tz?: string | null,
): string {
  if (!input) return '—'
  if (typeof input === 'string' && DATE_ONLY_RE.test(input)) {
    const d = new Date(`${input}T00:00:00`)
    return isValid(d) ? format(d, fmt) : '—'
  }
  const d = typeof input === 'string' ? new Date(input) : input
  if (!isValid(d)) return '—'
  return formatInTimeZone(d, resolveTz(tz), fmt)
}

/**
 * Parse an ISO string to a Date. Date-only strings (YYYY-MM-DD) are parsed as local
 * midnight to avoid the UTC day-shift bug.
 */
function toDate(input: string | Date | null | undefined): Date | null {
  if (!input) return null
  if (input instanceof Date) return isValid(input) ? input : null
  // Date-only → local midnight (avoids UTC shift in negative-offset timezones)
  if (DATE_ONLY_RE.test(input)) {
    const d = new Date(`${input}T00:00:00`)
    return isValid(d) ? d : null
  }
  const d = new Date(input)
  return isValid(d) ? d : null
}

/**
 * Format a date or ISO string as a local date. Date-only strings are rendered
 * in local time (never shifted). Datetime strings use the browser timezone.
 */
export function fmtDate(
  input: string | Date | null | undefined,
  fmt = 'MMM d, yyyy',
): string {
  if (!input) return '—'
  if (typeof input === 'string' && DATE_ONLY_RE.test(input)) {
    // Date-only: just format the local date directly — no tz conversion needed
    const d = new Date(`${input}T00:00:00`)
    return isValid(d) ? format(d, fmt) : '—'
  }
  const d = toDate(input)
  if (!d) return '—'
  return formatInTimeZone(d, localTz(), fmt)
}

/** Short date: "Apr 23" */
export function fmtDateShort(input: string | Date | null | undefined): string {
  return fmtDate(input, 'MMM d')
}

/** Long date: "April 23, 2026" */
export function fmtDateLong(input: string | Date | null | undefined): string {
  return fmtDate(input, 'MMMM d, yyyy')
}

/** Time only in local timezone: "9:00 AM" */
export function fmtTime(
  input: string | Date | null | undefined,
  fmt = 'h:mm a',
): string {
  const d = toDate(input)
  if (!d) return '—'
  return formatInTimeZone(d, localTz(), fmt)
}

/** Date + time: "Apr 23, 2026 9:00 AM" */
export function fmtDateTime(
  input: string | Date | null | undefined,
  fmt = 'MMM d, yyyy h:mm a',
): string {
  if (!input) return '—'
  if (typeof input === 'string' && DATE_ONLY_RE.test(input)) {
    const d = new Date(`${input}T00:00:00`)
    return isValid(d) ? format(d, fmt) : '—'
  }
  const d = toDate(input)
  if (!d) return '—'
  return formatInTimeZone(d, localTz(), fmt)
}

/**
 * Absolute instant of the next `hour`:00 wall-clock time in an IANA timezone.
 *
 * Used by the quiet-hours flow (AC-14): "schedule for 8 AM" must mean 8 AM
 * where the CONTACT is, not where the user is. The conversion works in two
 * steps, both anchored to the contact's zone:
 *
 *  1. Find the contact-zone calendar date to target: format `now` in `tz`
 *     to get the wall-clock date and hour over there; if it is already past
 *     `hour` o'clock there, roll to the next calendar day (plain date-only
 *     arithmetic in UTC — tz-agnostic since we only shift a calendar day).
 *  2. Convert "<targetDate>T<hour>:00:00 as a wall time IN `tz`" to a UTC
 *     instant with zonedTimeToUtc, which applies the zone's UTC offset FOR
 *     THAT DATE — so a DST transition between now and the target morning is
 *     accounted for. (Parsing the same string with `new Date(...)` would
 *     instead apply the USER's local offset — the bug this helper replaces.)
 *
 * Invalid/missing timezones fall back via resolveTz (workspace tz, then
 * America/Chicago) rather than throwing mid-flow.
 */
export function nextMorningInTimezone(tz: string, hour = 8, now: Date = new Date()): Date {
  const zone = resolveTz(tz)
  let dateStr = formatInTimeZone(now, zone, 'yyyy-MM-dd')
  const hourNowInZone = Number(formatInTimeZone(now, zone, 'H'))
  if (hourNowInZone >= hour) {
    // Past `hour`:00 in the contact's zone — target tomorrow's morning there.
    const d = new Date(`${dateStr}T00:00:00Z`)
    d.setUTCDate(d.getUTCDate() + 1)
    dateStr = d.toISOString().slice(0, 10)
  }
  return zonedTimeToUtc(`${dateStr}T${String(hour).padStart(2, '0')}:00:00`, zone)
}

/** "2 hours ago", "3 days ago" — relative, no tz needed */
export function fmtRelative(input: string | Date | null | undefined): string {
  const d = toDate(input)
  if (!d) return '—'
  return formatDistanceToNow(d, { addSuffix: true })
}

/** Full datetime for tooltips/titles: "April 23, 2026 at 9:00 AM" */
export function fmtDateTimeLong(input: string | Date | null | undefined): string {
  return fmtDateTime(input, "MMMM d, yyyy 'at' h:mm a")
}
